import { useMemo } from 'react'
import { CellContext, ColumnDef } from '@tanstack/react-table'
import {
  ColumnDefWithFilters,
  ColumnGroup,
  EnrichedTeamMembership,
} from '../types'
import {
  computeRankById,
  createRankColumn,
  gearStats,
  statPercentageToColor,
  useSimpleColumns,
} from '../utils'
import { useAudit } from '../context'
import { genericSorting } from 'app/frontend/base/config/column-definitions/util'
import AuditFilter, { useAuditFilterState } from '../components/AuditFilter'
import Select from 'app/frontend/base/components/Select'
import { Check, X } from 'lucide-react'
import {
  ItemEquipmentSlot,
  itemSlotLabels,
} from 'app/frontend/types/custom/ItemSlot'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import SocketIcon from 'app/frontend/base/pages/CharacterDashboard/components/SocketIcon'
import { cn } from '@/lib/utils'

const enchantableItems: ItemEquipmentSlot[] = [
  'main_hand',
  'off_hand',
  'head',
  'chest',
  'shoulder',
  'legs',
  'feet',
  'finger_1',
  'finger_2',
]

const hasData = (
  value: string | number | undefined,
): value is string | number => value !== '' && value !== undefined

const isBelowQuality = (
  value: string | number | undefined,
  minimumQuality: number,
) => hasData(value) && Number(value) < minimumQuality

const countInvalidGems = (
  member: EnrichedTeamMembership,
  minimumQuality: number,
) =>
  (member.data?.gem_list?.split('|') || []).filter((q) =>
    isBelowQuality(q, minimumQuality),
  ).length

const computeMissingTotal = (
  member: EnrichedTeamMembership,
  minimumQuality: number,
): number | undefined => {
  const emptySockets = member.data?.empty_sockets
  const rareGemsX =
    hasData(emptySockets) &&
    Number(emptySockets) + countInvalidGems(member, minimumQuality) > 0
      ? 1
      : 0

  const epicGemX = isBelowQuality(member.data?.epic_gem, minimumQuality) ? 1 : 0

  const enchantXCount = enchantableItems.filter((item) =>
    isBelowQuality(member.data?.[`enchant_quality_${item}`], minimumQuality),
  ).length

  return rareGemsX + epicGemX + enchantXCount
}

export const minimumQualityOptions = [
  {
    label: 'Casual',
    value: '2',
    description: 'Only gems and enchants from the current expansion are valid',
  },
  {
    label: 'Lenient',
    value: '3',
    description:
      "Only the highest quality gems and enchants are valid, unless they aren't output or tertiary related",
  },
  {
    label: 'Strict',
    value: '4',
    description:
      'Requires everything to be best-in-slot, even minor things like a small amount of Stamina or fall damage reduction',
  },
]

export const useMissingColumn = (header: string, className?: string) => {
  const { value: minimumQualityParam, setValue: setMinimumQuality } =
    useAuditFilterState<string>(
      'minimumQuality',
      '4',
      minimumQualityOptions.map((o) => o.value),
    )
  const minimumQuality = Number(minimumQualityParam)
  const { visibleMembers, showingRanks } = useAudit()

  const missingTotalById = useMemo(
    () =>
      Object.fromEntries(
        visibleMembers.map((member) => [
          member.id.toString(),
          computeMissingTotal(member, minimumQuality),
        ]),
      ) as Record<string, number | undefined>,
    [visibleMembers, minimumQuality],
  )

  const missingRankById = useMemo(
    () =>
      computeRankById(
        visibleMembers,
        (member) => -(missingTotalById[member.id.toString()] ?? 0),
      ),
    [visibleMembers, missingTotalById, minimumQuality],
  )

  const missingColumn: ColumnDef<EnrichedTeamMembership> = useMemo(
    () => ({
      id: 'missing',
      header,
      meta: {
        className: className ?? 'border-r',
      },
      columns: [
        ...(showingRanks
          ? [createRankColumn(missingRankById, 'missing_rank', '')]
          : []),
        {
          id: 'missing_total',
          ...genericSorting<EnrichedTeamMembership>(
            'alphanumeric',
            'Issues',
            true,
          ),
          meta: {
            className: className ? '' : 'border-r',
          },
          accessorFn: (row: EnrichedTeamMembership) =>
            missingTotalById[row.id.toString()] ?? 0,
          cell: (info: CellContext<EnrichedTeamMembership, unknown>) => {
            const total = missingTotalById[info.row.original.id.toString()]
            if (total === undefined) return ''
            return (
              <span
                className={
                  total === 0
                    ? 'text-success-foreground'
                    : 'text-destructive-foreground'
                }
              >
                {total}
              </span>
            )
          },
        },
      ],
    }),
    [missingTotalById, missingRankById, showingRanks, minimumQuality],
  )

  const minimumQualityFilter = useMemo(
    () => (
      <AuditFilter key='minimum_quality' label='Minimum quality'>
        <Select
          className='w-40'
          value={minimumQualityParam}
          setValue={(value) => setMinimumQuality(value ?? '4')}
          options={minimumQualityOptions}
        />
      </AuditFilter>
    ),
    [minimumQualityParam],
  )

  return { missingColumn, minimumQualityFilter, minimumQuality }
}

export const enchantViewOptions = [
  { label: 'Summary', value: 'summary' },
  { label: 'Full list', value: 'full' },
]

export const useEnchantmentsColumns = (): ColumnDefWithFilters => {
  const { missingColumn, minimumQualityFilter, minimumQuality } =
    useMissingColumn('')

  const { value: view, setValue: setView } = useAuditFilterState<
    'summary' | 'full'
  >(
    'enchantView',
    'summary',
    enchantViewOptions.map((o) => o.value),
  )

  const collectionGroups: ColumnGroup[] = [
    {
      id: 'gems',
      header: 'Gems',
      rankFields: null,
      subColumns: [
        {
          id: 'tertiary_sockets',
          header: 'Tertiary sockets',
          cellWrapper: (_, member) => {
            const jewelrySocketCount = (
              ['neck', 'finger_1', 'finger_2'] as const
            ).filter(
              (slot) => (member.gear?.[slot]?.sockets?.length ?? 0) > 0,
            ).length

            const tertiarySockets =
              (member.data?.gem_list?.split('|').length ?? 0) -
              jewelrySocketCount

            return (
              <span
                className={cn(
                  'whitespace-nowrap',
                  tertiarySockets >= 3
                    ? 'text-success-foreground'
                    : 'text-warning-foreground',
                )}
              >
                {tertiarySockets} / 3
              </span>
            )
          },
        },
        {
          id: 'rare_gems',
          header: view === 'full' ? '' : 'Rare gems',
          field: 'empty_sockets',
          cellWrapper: (emptySockets, member) => {
            const invalidGems = countInvalidGems(member, minimumQuality)
            const issues = Number(emptySockets) + invalidGems

            const gemList = Object.values(member.gear ?? {})
              .flatMap((item) => item.sockets)
              .filter((gem) => gem !== undefined)

            return view === 'full' ? (
              <span className='flex items-center gap-1'>
                {gemList.map((socket, index) => (
                  <SocketIcon key={index} socket={socket} />
                ))}
              </span>
            ) : (
              <Tooltip>
                <TooltipTrigger className='flex w-full items-center justify-center'>
                  {!hasData(emptySockets) ? (
                    ''
                  ) : issues === 0 ? (
                    <Check className='w-4 h-4 text-success-foreground' />
                  ) : (
                    <X className='w-4 h-4 text-destructive-foreground' />
                  )}
                </TooltipTrigger>
                {issues > 0 ? (
                  <TooltipContent>
                    {[
                      ...(Number(emptySockets) > 0
                        ? [
                            `${emptySockets} empty socket${
                              emptySockets === 1 ? '' : 's'
                            }`,
                          ]
                        : []),
                      ...(invalidGems > 0
                        ? [
                            `${invalidGems} invalid gem${
                              invalidGems === 1 ? '' : 's'
                            }`,
                          ]
                        : []),
                    ].join(' and ')}
                  </TooltipContent>
                ) : null}
              </Tooltip>
            )
          },
        },
        ...(view === 'full'
          ? []
          : [
              {
                id: 'epic_gem',
                header: 'Epic gem',
                field: 'epic_gem',
                cellWrapper: (value: string | number) => (
                  <span className='flex w-full items-center justify-center'>
                    {!hasData(value) ? (
                      ''
                    ) : isBelowQuality(value, minimumQuality) ? (
                      <X className='w-4 h-4 text-destructive-foreground' />
                    ) : (
                      <Check className='w-4 h-4 text-success-foreground' />
                    )}
                  </span>
                ),
              },
            ]),
      ],
    },
    {
      id: 'enchants',
      header: 'Enchants',
      rankFields: null,
      subColumns: enchantableItems.map((item) => ({
        id: item,
        header: itemSlotLabels[item],
        field: view === 'full' ? `${item}_enchant` : `enchant_quality_${item}`,
        cellWrapper: (value, member) => {
          if (view === 'full') {
            const hasIssue =
              hasData(member.data?.[`enchant_quality_${item}`]) &&
              isBelowQuality(
                member.data?.[`enchant_quality_${item}`],
                minimumQuality,
              )

            return (
              <span
                className={cn(
                  'whitespace-nowrap',
                  hasIssue ? 'text-destructive-foreground' : '',
                )}
              >
                {value ||
                  (hasIssue ? (
                    <span className='text-destructive-foreground italic'>
                      No enchant
                    </span>
                  ) : (
                    ''
                  ))}
              </span>
            )
          }

          return (
            <Tooltip>
              <TooltipTrigger className='flex w-full items-center justify-center'>
                {!hasData(value) ? (
                  ''
                ) : isBelowQuality(value, minimumQuality) ? (
                  <X className='w-4 h-4 text-destructive-foreground' />
                ) : (
                  <Check className='w-4 h-4 text-success-foreground' />
                )}
              </TooltipTrigger>
              <TooltipContent>
                {member.data?.[`${item}_enchant`] || (
                  <span className='text-muted-foreground italic'>
                    No enchant
                  </span>
                )}
              </TooltipContent>
            </Tooltip>
          )
        },
      })),
    },
    {
      id: 'stat_distribution',
      header: 'Enchant stat distribution (%)',
      rankFields: null,
      subColumns: gearStats.map((stat) => ({
        id: stat.value,
        header: () => (
          <Tooltip>
            <TooltipTrigger>{stat.label}</TooltipTrigger>
            <TooltipContent>{stat.fullLabel}</TooltipContent>
          </Tooltip>
        ),
        field: `${stat.value}_enchant_percentage`,
        cellWrapper: (value) => (
          <span className={cn(statPercentageToColor(Number(value)))}>
            {value}
          </span>
        ),
      })),
    },
  ]

  const simpleColumns = useSimpleColumns(collectionGroups, [
    minimumQuality,
    view,
  ])

  const columns: ColumnDef<EnrichedTeamMembership>[] = useMemo(
    () => [missingColumn, ...simpleColumns],
    [missingColumn, simpleColumns],
  )

  return {
    columns,
    initialSortBy: [{ id: 'missing_total', desc: true }],
    filters: [
      minimumQualityFilter,
      <AuditFilter key='view' label='View'>
        <Select
          className='w-32'
          value={view}
          setValue={(value) => setView(value as 'summary' | 'full')}
          options={enchantViewOptions}
        />
      </AuditFilter>,
    ],
  }
}
